import type { FC } from 'react';
import { lazy } from 'react';
import { useMemoOne } from 'use-memo-one';
import type {
  MyPortfolioParams,
  TransactionsParams,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMyPortfolioGraph } from 'src/apis/capacity-management/use-my-portfolio';
import { useMyTransactions } from 'src/apis/capacity-management/use-my-transactions';
import type { NetworkPoint } from 'src/apis/monolith/types';
import type { NetworkPointRequest } from 'src/apis/monolith/use-network-point';
import { useGetSurrenderActions } from 'src/apis/surrender-service-legacy/use-get-surrender-actions';
import { useOptionalSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useMyPortfolioPageParams } from 'src/pages/transport/network-points/details/my-portfolio-overview/use-page-params';

const Page = lazy(() => import('./page'));

type Props = {
  networkPoint: Successful<NetworkPointRequest>;
  capacityManagementRoot: Successful<CapacityManagementRootRequest>;
};

export const MyPortfolioOverview: FC<Props> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  return (
    <Content
      networkPoint={networkPoint.response.data}
      capacityManagementRoot={capacityManagementRoot}
    />
  );
};

type ContentProps = {
  networkPoint: NetworkPoint;
  capacityManagementRoot: Successful<CapacityManagementRootRequest>;
};

const Content: FC<ContentProps> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  const pageParams = useMyPortfolioPageParams();

  const transactionsParams = useMemoOne<TransactionsParams>(() => {
    const {
      start,
      pageSize,
      sortBy,
      sortDirection,
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    } = pageParams.value;

    return {
      offset: start,
      limit: pageSize,
      sortBy,
      sortDirection,
      status: ['SUCCESSFUL'],
      networkPointId: [networkPoint.networkPointUuid],
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const myTransactions = useMyTransactions({
    capacityManagementRoot,
    params: transactionsParams,
  });

  const surrenderRoot = useOptionalSurrenderRoot();
  const surrenderActions = useGetSurrenderActions(
    myTransactions,
    surrenderRoot
  );

  const paramsForGraph = useMemoOne<MyPortfolioParams>(() => {
    return {
      runtimeIntersectionFilterStart:
        pageParams.value.runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd:
        pageParams.value.runtimeIntersectionFilterEnd,
      granularity: pageParams.value.granularity,
      nwpId: networkPoint.networkPointUuid,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const myPortfolioGraph = useMyPortfolioGraph({
    capacityManagementRoot,
    params: paramsForGraph,
  });

  if (!surrenderRoot.loaded) return <PageSpinner />;

  const hasSurrenderActions = !!(
    surrenderRoot.response?.data._links.getDeals &&
    surrenderRoot.response.data._links.getTrades
  );

  return (
    <Page
      capacityManagementRoot={capacityManagementRoot}
      networkPoint={networkPoint}
      myTransactions={myTransactions}
      pageParams={pageParams}
      myPortfolioGraph={myPortfolioGraph}
      surrenderActions={hasSurrenderActions ? surrenderActions : undefined}
      isSurrenderForAllEnabled={
        !!surrenderRoot.response?.data._links.isSurrenderForAllEnabled
      }
    />
  );
};
